import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { profileSchema, type ProfileInput } from "@mnp/shared";
import { Icon, TgHeader, StickyFooter } from "../components/ui/index.js";
import { requestContact, isRequestContactSupported, hapticImpact } from "../lib/telegram.js";

export function OnboardingScreen() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2>(1);
  const [data, setData] = useState<ProfileInput>({
    fullName: "",
    phone: "",
    passportSeries: "",
    passportNumber: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [phoneFromTg, setPhoneFromTg] = useState(false);
  const [contactPending, setContactPending] = useState(false);
  const contactSupported = isRequestContactSupported();

  async function onShareContact() {
    setError(null);
    setContactPending(true);
    hapticImpact("light");
    try {
      const phone = await requestContact();
      if (phone) {
        setData((d) => ({ ...d, phone }));
        setPhoneFromTg(true);
      } else {
        setError("Не удалось получить номер. Можно ввести вручную.");
      }
    } finally {
      setContactPending(false);
    }
  }

  const progress = step === 1 ? 50 : 100;
  const canNext =
    step === 1
      ? data.fullName.trim().length > 3 && data.phone.trim().length > 8
      : data.passportNumber.trim().length >= 9;

  const save = useMutation({
    mutationFn: (d: ProfileInput) => api("/profile", { method: "POST", body: JSON.stringify(d) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      nav("/", { replace: true });
    },
    onError: (e: any) => setError(e.message ?? "Ошибка сохранения"),
  });

  function onNext() {
    setError(null);
    if (step === 1) {
      setStep(2);
      return;
    }
    const parsed = profileSchema.safeParse(data);
    if (!parsed.success) {
      setError(parsed.error.errors[0]?.message ?? "Проверьте поля");
      return;
    }
    save.mutate(parsed.data);
  }

  return (
    <div className="app-shell">
      <TgHeader title="Профиль" onBack={step === 2 ? () => setStep(1) : () => nav("/welcome")} />
      <div className="screen pad" style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: "var(--mnp-gray-100)", borderRadius: 2, overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: progress + "%",
              background: "var(--mnp-teal-600)",
              transition: "width .3s",
            }}
          />
        </div>
        <p className="t-small mt-2 mb-3">Шаг {step} из 2</p>
        <h2 className="t-h2 mb-1">{step === 1 ? "Личные данные" : "Паспорт"}</h2>
        <p className="t-body mb-4">
          {step === 1 ? "Нужны для оформления посылки." : "Нужен для отправки на таможне."}
        </p>

        {step === 1 ? (
          <>
            <div className="field">
              <label>ФИО</label>
              <input
                className="input"
                value={data.fullName}
                onChange={(e) => setData({ ...data, fullName: e.target.value })}
                placeholder="Иван Иванов"
              />
            </div>
            <div className="field">
              <label>Телефон</label>
              {contactSupported && (
                <button
                  type="button"
                  className="btn btn-secondary btn-block mb-2"
                  onClick={onShareContact}
                  disabled={contactPending}
                >
                  <Icon name="phone" size={16} />
                  <span style={{ marginLeft: 6 }}>
                    {contactPending ? "Запрашиваем…" : "Взять номер из Telegram"}
                  </span>
                </button>
              )}
              <input
                className="input"
                value={data.phone}
                onChange={(e) => {
                  setData({ ...data, phone: e.target.value });
                  if (phoneFromTg) setPhoneFromTg(false);
                }}
                placeholder="+7 ..."
                inputMode="tel"
              />
              {phoneFromTg && (
                <div className="help-ok mt-1" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="check" size={14} />
                  Подтверждён Telegram
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label>Серия и номер документа</label>
              <input
                className="input"
                value={data.passportNumber}
                maxLength={11}
                inputMode="text"
                autoCapitalize="characters"
                onChange={(e) =>
                  setData({ ...data, passportSeries: "", passportNumber: e.target.value })
                }
                placeholder="9 или 11 символов"
              />
              <div className="t-small mt-1" style={{ color: "var(--mnp-fg-3)" }}>
                Номер/серия документа без пробелов (9 или 11 символов).
              </div>
            </div>
            <div className="banner banner-info mt-2">
              <Icon name="infoCircle" size={16} />
              <div>Данные шифруются и используются только для оформления.</div>
            </div>
          </>
        )}

        {error && <div className="help-err mt-2">{error}</div>}
        <div style={{ flex: 1 }} />
        <StickyFooter>
          <button
            className="btn btn-primary btn-lg btn-block"
            disabled={!canNext || save.isPending}
            onClick={onNext}
          >
            {save.isPending ? "Сохраняем…" : step === 1 ? "Далее" : "Сохранить"}
          </button>
        </StickyFooter>
      </div>
    </div>
  );
}
